import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { Form, Input, Link, Table, Tooltip } from 'tdesign-react';
import { useMemoizedFn } from 'ahooks';
import ShadowCard from '@/components/ShadowCard';
import SvgIcon from '@/components/SvgIcon';
import { isVoid, renderByte, renderDateTime, renderNum, renderNumRate } from '@/utils/common';
import ColTip from '@/components/Tip';
import { getBrokerList } from '@/services/kafkaBrokerService';
import useTable from '@/hooks/useTable';
import Dashboard from '../Dashboard';
import Detail from './Detail';

const { FormItem } = Form;

export default function Body({ clusterName }) {
  const [visible, setVisible] = useState(false);
  const [currentRow, setCurrentRow] = useState({});

  const columns = [
    {
      title: 'BrokerID',
      colKey: 'brokerId',
      fixed: 'left',
      sorter: true,
      defaultSort: 'asc',
      cell: ({ row }) => <Detail detail={row} clusterName={clusterName} />,
      width: 120,
    },
    {
      colKey: 'host',
      sorter: true,
      title: '地址',
      cell: ({ row }) => `${row.host}:${row.port}`,
      width: 200,
    },
    {
      colKey: 'version',
      title: '版本',
      sorter: true,
      width: 100,
    },
    {
      colKey: 'topicPartitionCount',
      title: (
        <ColTip
          content={
            <>
              <strong>分区个数</strong>
              <li>指该broker实际承载了多个Topic，按最小粒度，子分区粒度。</li>
            </>
          }
        >
          分区个数
        </ColTip>
      ),
      width: 140,
      sorter: true,
      cell: renderNum,
    },

    {
      title: '消息大小',
      colKey: 'messageSize',
      cell: renderByte,
      width: 150,
      sorter: true,
    },
    {
      colKey: 'rateIn',
      title: '生产速率',
      width: 120,
      cell: renderNumRate,
      sorter: true,
    },
    {
      colKey: 'rateOut',
      title: '消费速率',
      width: 120,
      cell: renderNumRate,
      sorter: true,
    },
    {
      title: '开启机架感知',
      colKey: 'hasRack',
      cell: ({ row }) =>
        row.hasRack ? (
          <Tooltip content={row.racks}>
            <Link theme='primary' hover='color'>
              是
            </Link>
          </Tooltip>
        ) : (
          '否'
        ),
      width: 150,
      sorter: true,
    },
    {
      colKey: 'createTime',
      title: <ColTip content='第一次采集时间'>创建时间</ColTip>,
      sorter: true,
      cell: renderDateTime,
      defaultHide: true,
      width: 180,
    },
    {
      colKey: 'updateTime',
      sorter: true,
      title: <ColTip content='非实时，有分钟级延迟'>采集时间</ColTip>,
      cell: renderDateTime,
      width: 180,
    },
    {
      colKey: 'option',
      title: '操作',
      fixed: 'right',
      align: 'center',
      cell: ({ row }) => (
        <Icon
          type='presentation-chart-line'
          size={18}
          onClick={() => {
            setVisible(true);
            setCurrentRow(row);
          }}
        />
      ),
      width: 60,
    },
  ];

  const loadData = useMemoizedFn(async (params = {}) => {
    const { data } = await getBrokerList({ ...params, clusterName });
    setData(data.brokerMetricsList);
  });

  const { header, config, setData, doSearch } = useTable({
    search: (
      <FormItem name='host'>
        <Input placeholder='输入Broker地址' clearable />
      </FormItem>
    ),
    columns: columns.filter((item) => !isVoid(item)),
    onSearch: loadData,
  });

  useEffect(() => {
    if (clusterName) {
      doSearch();
    }
  }, [clusterName, doSearch]);

  const handleClose = () => {
    setVisible(false);
  };

  return (
    <Root>
      {header}
      <Table {...config} />
      <Dashboard
        clusterName={clusterName}
        detail={currentRow}
        visible={visible}
        handleClose={handleClose}
      />
    </Root>
  );
}

const Root = styled(ShadowCard)`
  flex: 1;
  margin-top: 12px;
`;

const Icon = styled(SvgIcon)`
  cursor: pointer;
  color: #194dff;
`;
